import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../../constants/helpRouteMap";
import {
  ByoCircuitPresetKey,
  ByoCircuitSettings,
  getByoCircuitSettings,
  previewWouldPause,
  updateByoCircuitSettings,
} from "../../../../helpers/byo_circuit_helper";
import { isOwner } from "../../../../utils/tenantRoles";

/** Barra taxa vs limiar — 100% = no limiar (pausaria). */
const ThresholdBar = ({
  label,
  rate,
  limit,
}: {
  label: string;
  rate: number;
  limit: number;
}) => {
  const pct =
    limit > 0 ? Math.min(100, Math.round((rate / limit) * 100)) : 0;
  const color =
    rate >= limit ? "danger" : pct >= 75 ? "warning" : "success";
  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between small mb-1">
        <span>{label}</span>
        <span className="text-muted">
          {rate}% / {limit}%
        </span>
      </div>
      <Progress value={pct} color={color} className="mb-0" style={{ height: 8 }} />
    </div>
  );
};

/**
 * Controles de envio BYO — limiares de pausa automática (circuit).
 * Presets + prévia 24h + histórico + confirmação ao desligar auto-retomada.
 */
const SendingControlsPage = () => {
  const { t, i18n } = useTranslation();
  const owner = isOwner();
  document.title = t("settings.sendingControls.documentTitle");
  const helpHref = helpUrlForRoute(
    "/settings/sending-controls",
    i18n.language || "pt-BR"
  );

  const [data, setData] = useState<ByoCircuitSettings | null>(null);
  const [loading, setLoading] = useState(owner);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bounce, setBounce] = useState("");
  const [complaint, setComplaint] = useState("");
  const [minSent, setMinSent] = useState("");
  const [autoResume, setAutoResume] = useState(true);
  const [confirmAutoOff, setConfirmAutoOff] = useState(false);
  const [confirmWouldPause, setConfirmWouldPause] = useState(false);

  const applyForm = (payload: ByoCircuitSettings) => {
    setData(payload);
    setBounce(String(payload.effective.bounce_pct));
    setComplaint(String(payload.effective.complaint_pct));
    setMinSent(String(payload.effective.min_sent));
    setAutoResume(!!payload.effective.auto_resume_enabled);
  };

  const load = useCallback(async () => {
    if (!owner) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getByoCircuitSettings();
      if (!res?.success) {
        setError(t("settings.sendingControls.loadError"));
        setData(null);
        return;
      }
      applyForm(res.data);
    } catch (e: any) {
      setError(e?.message || t("settings.sendingControls.loadError"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [owner, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const livePreview = useMemo(() => {
    if (!data?.recent_24h) return null;
    const b = Number(bounce);
    const c = Number(complaint);
    const m = Number(minSent);
    if (Number.isNaN(b) || Number.isNaN(c) || Number.isNaN(m)) return null;
    return previewWouldPause(data.recent_24h, b, c, m);
  }, [data?.recent_24h, bounce, complaint, minSent]);

  const doSave = async () => {
    if (!data?.available) return;
    setSaving(true);
    try {
      const res = await updateByoCircuitSettings({
        bounce_pct: Number(bounce),
        complaint_pct: Number(complaint),
        min_sent: Number(minSent),
        auto_resume_enabled: autoResume,
      });
      if (!res?.success) {
        toast.error(t("settings.sendingControls.saveError"));
        return;
      }
      applyForm(res.data);
      toast.success(t("settings.sendingControls.saved"));
    } catch (e: any) {
      toast.error(e?.message || t("settings.sendingControls.saveError"));
    } finally {
      setSaving(false);
      setConfirmAutoOff(false);
      setConfirmWouldPause(false);
    }
  };

  const save = () => {
    // Confirma só ao desligar retomada (evita surpresa operacional).
    if (data?.effective.auto_resume_enabled && !autoResume) {
      setConfirmAutoOff(true);
      return;
    }
    if (livePreview?.would_pause) {
      setConfirmWouldPause(true);
      return;
    }
    void doSave();
  };

  const confirmAutoOffContinue = () => {
    setConfirmAutoOff(false);
    if (livePreview?.would_pause) {
      setConfirmWouldPause(true);
      return;
    }
    void doSave();
  };

  const applyPreset = async (key: ByoCircuitPresetKey) => {
    if (!data?.available) return;
    setSaving(true);
    try {
      const res = await updateByoCircuitSettings({ preset: key });
      if (!res?.success) {
        toast.error(t("settings.sendingControls.saveError"));
        return;
      }
      applyForm(res.data);
      toast.success(t("settings.sendingControls.presetApplied", {
        name: t(`settings.sendingControls.preset.${key}`),
      }));
    } catch (e: any) {
      toast.error(e?.message || t("settings.sendingControls.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!data?.available) return;
    setSaving(true);
    try {
      const res = await updateByoCircuitSettings({ reset_to_defaults: true });
      if (!res?.success) {
        toast.error(t("settings.sendingControls.saveError"));
        return;
      }
      applyForm(res.data);
      toast.success(t("settings.sendingControls.resetOk"));
    } catch (e: any) {
      toast.error(e?.message || t("settings.sendingControls.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const formatHistoryAt = (iso: string | null) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(i18n.language || "pt-BR");
    } catch {
      return iso;
    }
  };

  if (!owner) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="warning">{t("settings.sendingControls.ownerOnly")}</Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <ToastContainer />
        <BreadCrumb
          title={t("settings.sendingControls.pageTitle")}
          pageTitle={t("settings.sendingControls.breadcrumb")}
          actions={
            helpHref ? (
              <PageHelpLink href={helpHref} label={t("common.help")} />
            ) : undefined
          }
        />
        <p className="text-muted mb-3">{t("settings.sendingControls.intro")}</p>

        <DismissiblePageTip tipId="sending-controls-tour-v1">
          {t("settings.sendingControls.tourTip")}
        </DismissiblePageTip>

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        )}

        {!loading && error && <Alert color="danger">{error}</Alert>}

        {!loading && data && !data.available && (
          <Card>
            <CardBody>
              <h5 className="mb-2">{t("settings.sendingControls.unavailableTitle")}</h5>
              <p className="text-muted mb-3">
                {t("settings.sendingControls.unavailableBody")}
              </p>
              <Button color="soft-primary" tag={Link} to="/settings/sending">
                {t("settings.sendingControls.backToSetup")}
              </Button>
            </CardBody>
          </Card>
        )}

        {!loading && data?.available && (
          <Row>
            <Col lg={8}>
              <Card>
                <CardBody>
                  <Alert color="info" className="mb-3">
                    {t("settings.sendingControls.awsReminder")}
                  </Alert>

                  <div className="mb-3">
                    <Label className="d-block mb-1">
                      {t("settings.sendingControls.presetsLabel")}
                    </Label>
                    <ButtonGroup size="sm">
                      {(["conservative", "recommended", "aggressive"] as ByoCircuitPresetKey[]).map(
                        (key) => (
                          <Button
                            key={key}
                            color="light"
                            disabled={
                              saving ||
                              (key === "aggressive" && !!data.aggressive_blocked)
                            }
                            title={
                              key === "aggressive" && data.aggressive_blocked
                                ? t("settings.sendingControls.aggressiveBlockedHint")
                                : undefined
                            }
                            onClick={() => void applyPreset(key)}
                          >
                            {t(`settings.sendingControls.preset.${key}`)}
                          </Button>
                        )
                      )}
                    </ButtonGroup>
                    {data.aggressive_blocked && (
                      <Alert color="warning" className="mt-2 mb-0 py-2">
                        {t("settings.sendingControls.aggressiveBlockedHint")}
                      </Alert>
                    )}
                    <div>
                      <small className="text-muted">
                        {t("settings.sendingControls.presetsHint")}
                      </small>
                    </div>
                  </div>

                  {(data.warnings || []).map((w) => (
                    <Alert
                      key={w.code}
                      color={
                        w.level === "danger"
                          ? "danger"
                          : w.level === "info"
                            ? "info"
                            : "warning"
                      }
                      className="mb-2"
                    >
                      {w.message}
                    </Alert>
                  ))}

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>{t("settings.sendingControls.bounceLabel")}</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min={data.bounds.bounce.min}
                          max={data.bounds.bounce.max}
                          value={bounce}
                          onChange={(e) => setBounce(e.target.value)}
                        />
                        <small className="text-muted">
                          {t("settings.sendingControls.bounceHint", {
                            min: data.bounds.bounce.min,
                            rec: data.bounds.bounce.recommended,
                            max: data.bounds.bounce.max,
                          })}
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>{t("settings.sendingControls.complaintLabel")}</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min={data.bounds.complaint.min}
                          max={data.bounds.complaint.max}
                          value={complaint}
                          onChange={(e) => setComplaint(e.target.value)}
                        />
                        <small className="text-muted">
                          {t("settings.sendingControls.complaintHint", {
                            min: data.bounds.complaint.min,
                            rec: data.bounds.complaint.recommended,
                            max: data.bounds.complaint.max,
                          })}
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>{t("settings.sendingControls.minSentLabel")}</Label>
                        <Input
                          type="number"
                          step="1"
                          min={data.bounds.min_sent.min}
                          max={data.bounds.min_sent.max}
                          value={minSent}
                          onChange={(e) => setMinSent(e.target.value)}
                        />
                        <small className="text-muted">
                          {t("settings.sendingControls.minSentHint", {
                            min: data.bounds.min_sent.min,
                            rec: data.bounds.min_sent.recommended,
                            max: data.bounds.min_sent.max,
                          })}
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup switch className="mt-4">
                        <Input
                          type="switch"
                          role="switch"
                          id="byo-auto-resume"
                          checked={autoResume}
                          onChange={(e) => setAutoResume(e.target.checked)}
                        />
                        <Label check for="byo-auto-resume">
                          {t("settings.sendingControls.autoResumeLabel")}
                        </Label>
                        <div>
                          <small className="text-muted">
                            {t("settings.sendingControls.autoResumeHint")}
                          </small>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <Button color="primary" disabled={saving} onClick={save}>
                      {saving ? <Spinner size="sm" /> : t("settings.sendingControls.save")}
                    </Button>
                    <Button
                      color="light"
                      disabled={saving || data.values.using_platform_defaults}
                      onClick={() => void resetDefaults()}
                    >
                      {t("settings.sendingControls.reset")}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {(data.history?.length ?? 0) > 0 && (
                <Card>
                  <CardBody>
                    <h6 className="mb-3">{t("settings.sendingControls.historyTitle")}</h6>
                    <div className="table-responsive">
                      <Table size="sm" className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>{t("settings.sendingControls.historyWhen")}</th>
                            <th>{t("settings.sendingControls.historyAction")}</th>
                            <th>{t("settings.sendingControls.historyDetail")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.history!.map((h, idx) => {
                            const eff = (h.new?.effective || {}) as {
                              bounce_rate?: number;
                              complaint_rate?: number;
                            };
                            return (
                              <tr key={`${h.at}-${idx}`}>
                                <td className="small text-nowrap">
                                  {formatHistoryAt(h.at)}
                                </td>
                                <td className="small">
                                  {t(
                                    h.action === "byo_circuit.reset"
                                      ? "settings.sendingControls.historyReset"
                                      : "settings.sendingControls.historyUpdated"
                                  )}
                                </td>
                                <td className="small text-muted">
                                  {h.summary ||
                                    (eff.bounce_rate != null
                                      ? t("settings.sendingControls.historyValues", {
                                          bounce: eff.bounce_rate,
                                          complaint: eff.complaint_rate ?? "—",
                                        })
                                      : "—")}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              )}
            </Col>
            <Col lg={4}>
              <Card>
                <CardBody>
                  <h6 className="mb-2">{t("settings.sendingControls.previewTitle")}</h6>
                  {data.recent_24h && data.recent_24h.sent === 0 ? (
                    <Alert color="info" className="mb-0">
                      <p className="mb-2">
                        {t("settings.sendingControls.previewEmpty")}
                      </p>
                      <Button
                        color="soft-primary"
                        size="sm"
                        tag={Link}
                        to="/campaigns"
                      >
                        {t("settings.sendingControls.previewEmptyCta")}
                      </Button>
                    </Alert>
                  ) : (
                    <>
                      {data.recent_24h && (
                        <>
                          <ul className="mb-2 small ps-3">
                            <li>
                              {t("settings.sendingControls.previewSent", {
                                value: data.recent_24h.sent,
                              })}
                            </li>
                          </ul>
                          <ThresholdBar
                            label={t("settings.sendingControls.barBounce")}
                            rate={data.recent_24h.bounce_pct}
                            limit={Number(bounce) || data.effective.bounce_pct}
                          />
                          <ThresholdBar
                            label={t("settings.sendingControls.barComplaint")}
                            rate={data.recent_24h.complaint_pct}
                            limit={
                              Number(complaint) || data.effective.complaint_pct
                            }
                          />
                        </>
                      )}
                      {livePreview && (
                        <Alert
                          color={
                            !livePreview.enough_volume
                              ? "secondary"
                              : livePreview.would_pause
                                ? "warning"
                                : "success"
                          }
                          className="mb-0 py-2 mt-2"
                        >
                          {!livePreview.enough_volume
                            ? t("settings.sendingControls.previewLowVolume")
                            : livePreview.would_pause
                              ? t("settings.sendingControls.previewWouldPause")
                              : t("settings.sendingControls.previewOk")}
                        </Alert>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <h6 className="mb-2">{t("settings.sendingControls.effectiveTitle")}</h6>
                  <ul className="mb-0 small ps-3">
                    <li>
                      {t("settings.sendingControls.effectiveBounce", {
                        value: data.effective.bounce_pct,
                      })}
                    </li>
                    <li>
                      {t("settings.sendingControls.effectiveComplaint", {
                        value: data.effective.complaint_pct,
                      })}
                    </li>
                    <li>
                      {t("settings.sendingControls.effectiveMinSent", {
                        value: data.effective.min_sent,
                      })}
                    </li>
                    <li>
                      {t(
                        data.effective.auto_resume_enabled
                          ? "settings.sendingControls.effectiveAutoOn"
                          : "settings.sendingControls.effectiveAutoOff"
                      )}
                    </li>
                  </ul>
                  <p className="text-muted small mt-3 mb-0">
                    {t("settings.sendingControls.sourceHint", {
                      source:
                        data.effective.source === "byo_override"
                          ? t("settings.sendingControls.sourceOverride")
                          : t("settings.sendingControls.sourcePlatform"),
                    })}
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        <Modal isOpen={confirmAutoOff} toggle={() => setConfirmAutoOff(false)} centered>
          <ModalHeader toggle={() => setConfirmAutoOff(false)}>
            {t("settings.sendingControls.confirmAutoOffTitle")}
          </ModalHeader>
          <ModalBody>{t("settings.sendingControls.confirmAutoOffBody")}</ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setConfirmAutoOff(false)}>
              {t("settings.sendingControls.confirmAutoOffCancel")}
            </Button>
            <Button color="warning" disabled={saving} onClick={confirmAutoOffContinue}>
              {t("settings.sendingControls.confirmAutoOffConfirm")}
            </Button>
          </ModalFooter>
        </Modal>

        <Modal
          isOpen={confirmWouldPause}
          toggle={() => setConfirmWouldPause(false)}
          centered
        >
          <ModalHeader toggle={() => setConfirmWouldPause(false)}>
            {t("settings.sendingControls.confirmWouldPauseTitle")}
          </ModalHeader>
          <ModalBody>{t("settings.sendingControls.confirmWouldPauseBody")}</ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setConfirmWouldPause(false)}>
              {t("settings.sendingControls.confirmWouldPauseCancel")}
            </Button>
            <Button color="warning" disabled={saving} onClick={() => void doSave()}>
              {t("settings.sendingControls.confirmWouldPauseConfirm")}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default SendingControlsPage;
